/**
 * 100. Billing / Subscription — plan, usage, invoices, payment method; tier
 * limitation comparison.
 *
 * Arabic and English (C7): every word from the catalogs; the plan and the
 * subscription's status are words, never the stored value; counts and
 * dates are written by the locale.
 */
import {
  Card,
  PageHeader,
  Badge,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiQuery } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { statusLabel, valueLabel } from "@/lib/i18n/records";
import { formatDate, formatMoney, formatQuantity } from "@/lib/money";

interface BillingData {
  planTier: string;
  status: "Active" | "PastDue" | "Cancelled" | "Trial";
  currentPeriodStart: string;
  currentPeriodEnd: string;
  usage: {
    users: number;
    apiRequests: number;
    storageBytes: number;
    aiCredits: number;
  };
  invoices: Array<{
    id: string;
    number: string;
    date: string;
    amount: string;
    /** The invoice's own currency (CUR-1). */
    currencyCode: string;
    status: "Paid" | "Open" | "Overdue";
  }>;
  paymentMethod: { brand: string; last4: string } | null;
}

export function BillingPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiQuery<BillingData>(
    ["billing", "overview"],
    "/billing/overview",
  );

  return (
    <div>
      <PageHeader
        title={t("admin.billing.title")}
        subtitle={t("admin.billing.subtitle")}
        actions={
          <PendingButton reason="editions" slice="EDN-4">
            {t("admin.billing.compare")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={8} />
      ) : (
        <>
          <div className="atlas-grid atlas-grid--4 mb-4">
            <Card title={t("admin.billing.plan")}>
              <div className="atlas-stat__value">
                {valueLabel(t, "planTier", data.planTier)}
              </div>
              <Badge tone={data.status === "Active" ? "success" : "warning"}>
                {statusLabel(t, "Subscription", data.status)}
              </Badge>
            </Card>
            <Card title={t("admin.billing.users")}>
              <div className="atlas-stat__value">
                {formatQuantity(data.usage.users, 0)}
              </div>
            </Card>
            <Card title={t("admin.billing.apiRequests")}>
              <div className="atlas-stat__value">
                {formatQuantity(data.usage.apiRequests, 0)}
              </div>
            </Card>
            <Card title={t("admin.billing.paymentMethod")}>
              {data.paymentMethod ? (
                <div className="atlas-stat__value" dir="ltr">
                  {data.paymentMethod.brand} •••• {data.paymentMethod.last4}
                </div>
              ) : (
                <div className="atlas-muted">{t("admin.billing.none")}</div>
              )}
            </Card>
          </div>
          <Card title={t("admin.billing.invoices")}>
            <DataTable
              rows={data.invoices}
              keyField="id"
              columns={[
                {
                  key: "number",
                  label: t("fields.number"),
                  render: (r) => <span className="atlas-mono">{r.number}</span>,
                },
                {
                  key: "date",
                  label: t("fields.date"),
                  render: (r) => formatDate(r.date),
                },
                {
                  key: "amount",
                  label: t("fields.amount"),
                  numeric: true,
                  render: (r) => formatMoney(r.amount, r.currencyCode),
                },
                {
                  key: "status",
                  label: t("fields.status"),
                  render: (r) => (
                    <StatusBadge status={r.status} type="SubscriptionInvoice" />
                  ),
                },
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
}
